import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as forumApi from '../api/forum';
import { usePagedList } from '../lib/usePagedList';
import {
  ErrorAlert,
  Spinner,
  EmptyState,
  Field,
  PagedListFooter,
  RelativeTime,
  UserChip,
} from '../components/ui';
import { cn, ui } from '../lib/ui';

const SEARCH_PAGE_SIZE = 25;
const MIN_QUERY_LENGTH = 2;

export default function ForumSearch() {
  const [params, setParams] = useSearchParams();
  const initialQuery = params.get('q') || '';
  const [inputValue, setInputValue] = useState(initialQuery);
  const searchTerm = (params.get('q') || '').trim();

  const list = usePagedList(
    cursor => {
      if (searchTerm.length < MIN_QUERY_LENGTH) {
        return Promise.resolve({ items: [], lastEvaluatedKey: null });
      }
      return forumApi
        .search({ q: searchTerm, limit: SEARCH_PAGE_SIZE, lastEvaluatedKey: cursor })
        .then(d => ({
          items: [
            ...(d.threads || []).map(t => ({ ...t, _kind: 'thread' })),
            ...(d.posts || []).map(p => ({ ...p, _kind: 'post' })),
          ],
          lastEvaluatedKey: d.lastEvaluatedKey,
        }));
    },
    [searchTerm]
  );

  function onSubmit(event) {
    event.preventDefault();
    const q = inputValue.trim();
    if (q.length < MIN_QUERY_LENGTH) return;
    setParams({ q });
  }

  const threads = list.items.filter(i => i._kind === 'thread');
  const posts = list.items.filter(i => i._kind === 'post');
  const hasSearched = searchTerm.length >= MIN_QUERY_LENGTH;
  const hasResults = threads.length > 0 || posts.length > 0;

  return (
    <>
      <p>
        <Link to="/forum">← Forum</Link>
      </p>
      <h1 className={ui.h1}>Search the forum</h1>

      <form onSubmit={onSubmit} className={cn(ui.row, 'mb-4')} role="search">
        <Field
          label="Search threads and posts"
          hint="At least 2 characters."
          style={{ minWidth: '18rem' }}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
        />
        <button
          className={ui.btnPrimary}
          type="submit"
          disabled={inputValue.trim().length < MIN_QUERY_LENGTH}
        >
          Search
        </button>
      </form>

      <ErrorAlert error={list.error} />

      {!hasSearched ? (
        <EmptyState>Enter at least {MIN_QUERY_LENGTH} characters and press Search.</EmptyState>
      ) : list.loading ? (
        <Spinner />
      ) : !hasResults && !list.error ? (
        <EmptyState>No results for &ldquo;{searchTerm}&rdquo;.</EmptyState>
      ) : (
        <>
          {threads.length > 0 && (
            <section className="mb-4">
              <h3 className={ui.h3}>Threads</h3>
              <div className={cn(ui.card, ui.tableWrap)}>
                <table className={ui.table}>
                  <thead>
                    <tr>
                      <th>Thread</th>
                      <th>Category</th>
                      <th>Author</th>
                      <th>Started</th>
                    </tr>
                  </thead>
                  <tbody>
                    {threads.map(t => (
                      <tr key={`thread-${t.threadId}`}>
                        <td>
                          <Link to={`/forum/threads/${t.threadId}`}>{t.title}</Link>
                        </td>
                        <td>{t.categoryName || '—'}</td>
                        <td>
                          <UserChip userId={t.createdBy} name={t.createdByName} />
                        </td>
                        <td>
                          <RelativeTime iso={t.createdAt} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {posts.length > 0 && (
            <section>
              <h3 className={ui.h3}>Posts</h3>
              {posts.map(p => (
                <Link
                  key={`post-${p.postId}`}
                  to={`/forum/threads/${p.threadId}`}
                  className={cn(ui.card, 'mb-4 block text-inherit no-underline')}
                >
                  <p className={cn(ui.muted, 'text-body-s')}>
                    In <strong>{p.threadTitle}</strong> — {p.categoryName || 'Unknown category'} —{' '}
                    <UserChip userId={p.authorId} name={p.authorName} /> —{' '}
                    <RelativeTime iso={p.createdAt} />
                  </p>
                  <p>{p.snippet}</p>
                </Link>
              ))}
            </section>
          )}

          <PagedListFooter
            hasMore={list.hasMore}
            loadingMore={list.loadingMore}
            onLoadMore={list.loadMore}
            endLabel="No more results."
          />
        </>
      )}
    </>
  );
}
